import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Underline } from "@tiptap/extension-underline";
import { useEffect } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, Undo2, Redo2, Link2,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";

// Recruitment-specific placeholders
const PLACEHOLDERS = [
  { label: "Candidate Name", value: "{{candidate_name}}" },
  { label: "Position Title", value: "{{position_title}}" },
  { label: "Company Name", value: "{{company_name}}" },
  { label: "Application Date", value: "{{application_date}}" },
  { label: "Status Label", value: "{{status_label}}" },
  { label: "Interview Date", value: "{{interview_date}}" },
];

function Sep() {
  return <div className="w-px h-5 bg-border mx-0.5 shrink-0" />;
}

function ToolBtn({
  active, disabled, onClick, title, children,
}: {
  active?: boolean; disabled?: boolean; onClick: () => void; title: string; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`flex items-center justify-center h-7 w-7 rounded text-sm transition-colors shrink-0
        ${active ? "bg-primary/15 text-primary" : "text-foreground/70 hover:bg-muted hover:text-foreground"}
        ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}
      `}
    >
      {children}
    </button>
  );
}

interface EmailBodyEditorProps {
  value: string;
  onChange: (html: string) => void;
}

export function EmailBodyEditor({ value, onChange }: EmailBodyEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false }),
      TextAlign.configure({ types: ["paragraph"] }),
      TextStyle,
      Underline,
    ],
    content: value || "<p></p>",
    editorProps: {
      attributes: {
        class: "outline-none min-h-[220px] leading-relaxed text-foreground text-sm px-4 py-3",
        spellcheck: "true",
      },
    },
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
  });

  // Sync external value changes (e.g. when dialog re-opens)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) {
      editor.commands.setContent(value || "<p></p>", { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="rounded-xl border border-[#d0d5e8] overflow-hidden bg-white dark:bg-card">
      {/* Toolbar */}
      <div className="flex items-center flex-wrap gap-0.5 px-2 py-1.5 border-b border-[#d0d5e8] bg-muted/30">
        <ToolBtn title="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
          <Undo2 className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn title="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
          <Redo2 className="h-3.5 w-3.5" />
        </ToolBtn>

        <Sep />

        <ToolBtn title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn title="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <UnderlineIcon className="h-3.5 w-3.5" />
        </ToolBtn>

        <Sep />

        <ToolBtn title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolBtn>

        <Sep />

        <ToolBtn title="Align left" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}>
          <AlignLeft className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn title="Align center" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}>
          <AlignCenter className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn title="Align right" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}>
          <AlignRight className="h-3.5 w-3.5" />
        </ToolBtn>

        <Sep />

        {/* Insert placeholder */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1 h-7 px-2 rounded text-xs font-medium text-foreground/70 hover:bg-muted hover:text-foreground transition-colors cursor-pointer"
            >
              <Link2 className="h-3.5 w-3.5" />
              Insert placeholder
              <ChevronDown className="h-3 w-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {PLACEHOLDERS.map((p) => (
              <DropdownMenuItem
                key={p.value}
                onSelect={() => editor.chain().focus().insertContent(p.value).run()}
              >
                <span className="text-xs font-mono text-muted-foreground mr-2">{p.value}</span>
                {p.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Editor area */}
      <EditorContent editor={editor} />

      {/* Tiptap prose styles */}
      <style>{`
        .tiptap p { margin: 0.4em 0; }
        .tiptap ul { list-style-type: disc; padding-left: 1.4em; margin: 0.4em 0; }
        .tiptap ol { list-style-type: decimal; padding-left: 1.4em; margin: 0.4em 0; }
        .tiptap li { margin: 0.2em 0; }
        .tiptap p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #adb5bd;
          pointer-events: none;
          height: 0;
        }
      `}</style>
    </div>
  );
}
